import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { authApi } from '../services/api';
import { useAuthStore } from '../store/auth.store';
import { LOCALE_LABELS, SUPPORTED_LOCALES, type Locale, isSupportedLocale } from '../i18n';
import FlagIcon from './FlagIcon';

interface Props {
  variant?: 'menu' | 'compact';
}

/**
 * Language picker. In `menu` variant, renders a stack of buttons (one per
 * supported locale) intended to live inside the user dropdown menu. The
 * `compact` variant is a single icon-button (not used yet, kept for future).
 *
 * Persists the choice to user.language via PATCH /api/auth/profile so the
 * preference follows the user across sessions and devices.
 */
export default function LanguageSwitcher({ variant = 'menu' }: Props) {
  const { i18n, t } = useTranslation();
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  const current = isSupportedLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'fr';

  const handleChange = async (locale: Locale) => {
    // We may need to persist even when the *displayed* locale already matches
    // the click — e.g. the i18n cache is "it" but user.language in the DB is
    // still "fr" because a previous PATCH never landed. Skip only when both
    // the UI and the user's stored preference are already on this locale.
    const alreadyApplied = locale === current && user?.language === locale;
    if (alreadyApplied) return;

    // Persist server-side FIRST so the DB is the source of truth on next
    // login. Surface a console warning if it fails — the previous best-effort
    // try/catch was hiding cases where the language never made it to the DB
    // and so didn't survive logout.
    if (user) {
      try {
        await authApi.updateProfile({ language: locale });
        setUser({ ...user, language: locale });
      } catch (err) {
        console.warn('[LanguageSwitcher] failed to persist language to backend', err);
      }
    }
    if (locale !== current) {
      await i18n.changeLanguage(locale);
    }
  };

  if (variant === 'compact') {
    return (
      <button
        type="button"
        title={LOCALE_LABELS[current]}
        className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
        onClick={() => {
          // Cycle through locales when the standalone button is clicked.
          const idx = SUPPORTED_LOCALES.indexOf(current);
          const next = SUPPORTED_LOCALES[(idx + 1) % SUPPORTED_LOCALES.length];
          void handleChange(next);
        }}
      >
        <FlagIcon locale={current} size={20} />
      </button>
    );
  }

  return (
    <div className="px-4 py-2">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
        <Globe className="h-3.5 w-3.5" />
        {t('layout.user_menu.language')}
      </div>
      <div className="flex items-center justify-center gap-3">
        {SUPPORTED_LOCALES.map((loc) => (
          <button
            key={loc}
            type="button"
            onClick={() => void handleChange(loc)}
            title={LOCALE_LABELS[loc]}
            aria-label={LOCALE_LABELS[loc]}
            aria-pressed={current === loc}
            className={`rounded-full transition ${
              current === loc
                ? 'ring-2 ring-cyan-500 ring-offset-2'
                : 'opacity-60 hover:opacity-100 hover:ring-2 hover:ring-gray-300 hover:ring-offset-1'
            }`}
          >
            <FlagIcon locale={loc} size={28} className="block rounded-full" />
          </button>
        ))}
      </div>
    </div>
  );
}
